import styled from "styled-components";
import { useState } from "react";
import { router, Head } from "@inertiajs/react";
import {
  ExternalLink,
  MessageCircle,
  RefreshCw,
  RotateCcw,
  Ban,
  Mail,
  FileText,
  Filter,
  X,
} from "lucide-react";

import { theme } from "../_kit/theme";
import PageShell from "../_kit/PageShell";
import ConfirmDialog from "../_kit/ConfirmDialog";
import { formatDateTime } from "../_kit/dateFormat";
import { apiFetch } from "../_kit/api";
import { useT } from "../_kit/i18n";

const Header = styled.div`
  margin-bottom: 1rem;
`;

const Title = styled.h1`
  font-size: 1.4rem;
  margin: 0;
`;

const Subtitle = styled.p`
  color: ${theme.muted};
  font-size: 0.9rem;
  margin: 0.25rem 0 0;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 12px;
  margin-bottom: 1.25rem;
  padding: 1rem;
`;

const Grid = styled.div`
  display: grid;
  gap: 0.7rem;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  font-size: 0.78rem;
  font-weight: 700;
  gap: 0.25rem;
  color: ${theme.ink700};
`;

const Input = styled.input`
  border: 1px solid ${theme.border};
  border-radius: 8px;
  font-size: 0.95rem;
  padding: 0.5rem 0.7rem;
`;

const TextArea = styled.textarea`
  border: 1px solid ${theme.border};
  border-radius: 8px;
  font-size: 0.95rem;
  min-height: 64px;
  padding: 0.5rem 0.7rem;
  resize: vertical;
`;

const Button = styled.button`
  align-items: center;
  background: ${theme.ink};
  border: 0;
  border-radius: 8px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  font-weight: 700;
  gap: 0.35rem;
  padding: 0.55rem 1rem;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

// Every row action gets its OWN colour + icon rather than a row of identical grey pills. At a desk
// the FO learns the shape, not the label — "the blue one is the link, the red one cancels" — which is
// what makes a dense row scannable under pressure.
const TONES = {
  link: { fg: "#2f57cc", bg: "#eef3fb", br: "#cddcf7" },
  wa: { fg: "#1b6e56", bg: "#e6f6ee", br: "#c4e8d6" },
  sync: { fg: "#8a5a00", bg: "#fdf3e3", br: "#f0dfc0" },
  recreate: { fg: "#6d28d9", bg: "#f5f3ff", br: "#ddd6fe" },
  danger: { fg: "#b42318", bg: "#fdecea", br: "#f7cfca" },
  neutral: { fg: "#4a4f63", bg: "#f4f2ee", br: "#e4e0d8" },
};

const TinyButton = styled.button`
  align-items: center;
  background: ${(props) => (TONES[props.$tone] || TONES.neutral).bg};
  border: 1px solid ${(props) => (TONES[props.$tone] || TONES.neutral).br};
  border-radius: 7px;
  color: ${(props) => (TONES[props.$tone] || TONES.neutral).fg};
  cursor: pointer;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 800;
  gap: 0.28rem;
  margin: 0 0.35rem 0.35rem 0;
  padding: 0.32rem 0.6rem;
  text-decoration: none;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const FilterBar = styled.div`
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 0.8rem;
`;

const FilterField = styled.label`
  color: ${theme.ink700};
  display: flex;
  flex-direction: column;
  font-size: 0.72rem;
  font-weight: 800;
  gap: 0.2rem;
`;

const SmallInput = styled.input`
  border: 1px solid ${theme.border};
  border-radius: 7px;
  font-size: 0.85rem;
  padding: 0.35rem 0.5rem;
`;

const SmallSelect = styled.select`
  border: 1px solid ${theme.border};
  border-radius: 7px;
  font-size: 0.85rem;
  padding: 0.35rem 0.5rem;
`;

const CheckLabel = styled.label`
  align-items: center;
  color: ${theme.ink700};
  display: flex;
  font-size: 0.8rem;
  font-weight: 700;
  gap: 0.3rem;
  padding-bottom: 0.4rem;
`;

const PageInfo = styled.div`
  color: #4a4f63;
  font-size: 0.8rem;
  font-weight: 700;
`;

const Row = styled.div`
  border-bottom: 1px solid ${theme.border};
  padding: 0.8rem 0;
  &:last-child {
    border-bottom: 0;
  }
`;

const RowTop = styled.div`
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: space-between;
`;

const Desc = styled.span`
  color: ${theme.ink};
  font-weight: 700;
`;

const Amount = styled.span`
  color: ${theme.ink};
  font-size: 1.05rem;
  font-weight: 800;
`;

// Dark ink rather than the muted grey: these lines carry the payer and who raised the charge.
const Meta = styled.div`
  color: #4a4f63;
  font-size: 0.8rem;
  margin-top: 0.2rem;
`;

const Chip = styled.span`
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  padding: 0.12rem 0.5rem;
  text-transform: uppercase;
  ${(props) => {
    if (props.$status === "paid") return "background:#e6f6ee;color:#1b6e56;";
    if (props.$status === "pending") return "background:#eef3fb;color:#2f4a70;";
    if (props.$status === "canceled")
      return "background:#f4f2ee;color:#5e6278;";
    return "background:#fdf3e3;color:#8a5a00;";
  }}
`;

const Note = styled.div`
  background: #fdf3e3;
  border: 1px solid #f0dfc0;
  border-radius: 8px;
  color: #8a5a00;
  font-size: 0.82rem;
  font-weight: 600;
  margin-bottom: 1rem;
  padding: 0.55rem 0.7rem;
`;

const Msg = styled.div`
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 700;
  margin-bottom: 0.8rem;
  padding: 0.5rem 0.7rem;
  ${(props) =>
    props.$ok
      ? "background:#e6f6ee;color:#1b6e56;"
      : "background:#fdecea;color:#b42318;"}
`;

const Pager = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin-top: 1rem;
`;

const CustomPaymentsPage = (props) => {
  const { t } = useT();
  const config = props.config || {};
  const [rows, setRows] = useState(props.payments || []);
  const [form, setForm] = useState({
    amount: "",
    description: "",
    payer_name: "",
    payer_phone: "",
    payer_email: "",
  });
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const fmt = (value) => Number(value || 0).toLocaleString("id-ID");
  const digits = (value) => String(value || "").replace(/\D/g, "");

  const setField = (key) => (event) =>
    setForm({ ...form, [key]: event.target.value });

  const replaceRow = (payment) =>
    setRows((prev) => {
      const index = prev.findIndex((row) => row.id === payment.id);
      if (index === -1) return [payment, ...prev];
      const next = [...prev];
      next[index] = payment;
      return next;
    });

  const post = async (key, path, body = {}) => {
    if (busy) return null;
    setBusy(key);
    setMsg(null);
    try {
      const res = await apiFetch(path, {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (res?.data?.payment) {
        replaceRow(res.data.payment);
        setMsg({ ok: true, text: res.message });
        if (res.data.wa_url) {
          window.open(res.data.wa_url, "_blank", "noopener,noreferrer");
        }
      } else {
        setMsg({ ok: false, text: res?.message || t("error_body") });
      }
      setBusy(null);
      return res;
    } catch {
      setMsg({ ok: false, text: t("error_body") });
      setBusy(null);
      return null;
    }
  };

  const amountValue = Number(digits(form.amount) || 0);
  const canSubmit =
    amountValue >= (config.min_idr || 0) &&
    form.description.trim().length >= 3 &&
    form.payer_name.trim().length > 0;

  // A confirmation restating the formatted amount and the description, because this is the one
  // screen where a typed number becomes a real charge and the predictable mistake is an extra zero.
  const submit = () =>
    setConfirm({
      title: t("cp_confirm_title"),
      body: `${t("cp_confirm_body")} Rp${fmt(amountValue)} — ${form.description.trim()}`,
      onConfirm: async () => {
        setConfirm(null);
        const res = await post("create", "/frontdesk_v2/custom_payments", {
          ...form,
          amount: digits(form.amount),
        });
        if (res?.data?.payment) {
          setForm({
            amount: "",
            description: "",
            payer_name: "",
            payer_phone: "",
            payer_email: "",
          });
        }
      },
    });

  const rowAction = (row, action, extra = {}) =>
    post(
      `${action}:${row.id}`,
      `/frontdesk_v2/custom_payments/${row.id}/${action}`,
      extra,
    );

  const voidRow = (row) =>
    setConfirm({
      title: t("cp_void_title"),
      body: `${t("cp_void_body")} ${row.amount_formatted} — ${row.description}`,
      onConfirm: () => {
        setConfirm(null);
        rowAction(row, "void");
      },
    });

  const recreateRow = (row) =>
    setConfirm({
      title: t("cp_recreate_title"),
      body: t("cp_recreate_body"),
      onConfirm: () => {
        setConfirm(null);
        rowAction(row, "recreate");
      },
    });

  const editEmail = (row) => {
    const email = window.prompt(t("cp_email_prompt"), row.payer_email || "");
    if (email === null) return;
    rowAction(row, "update_email", { email });
  };

  // Filters live in the URL, not component state: a shift hand-over can paste the link, and the back
  // button behaves. Every navigation carries the current filter set forward, so paging never silently
  // drops the filter the FO just applied.
  const [filters, setFilters] = useState(props.filters || {});
  const setFilter = (key) => (event) =>
    setFilters({
      ...filters,
      [key]:
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value,
    });

  const query = (extra = {}) => {
    const merged = { ...filters, ...extra };
    const out = {};
    Object.entries(merged).forEach(([key, value]) => {
      if (
        value !== "" &&
        value !== null &&
        value !== undefined &&
        value !== false
      ) {
        out[key] = value;
      }
    });
    return out;
  };

  const applyFilters = () =>
    router.get("/frontdesk_v2/custom_payments", query({ page: 1 }), {
      preserveState: false,
    });

  const clearFilters = () =>
    router.get("/frontdesk_v2/custom_payments", {}, { preserveState: false });

  const go = (page) =>
    router.get("/frontdesk_v2/custom_payments", query({ page }), {
      preserveState: false,
    });

  const pagination = props.pagination || { page: 1, pages: 1 };

  return (
    <PageShell>
      <Head title={t("cp_title")} />
      <Header>
        <Title>{t("cp_title")}</Title>
        <Subtitle>{t("cp_subtitle")}</Subtitle>
      </Header>

      <Note>{t("cp_no_refund_note")}</Note>
      {msg ? <Msg $ok={msg.ok}>{msg.text}</Msg> : null}

      <Card>
        <Grid>
          <Field>
            {t("cp_amount")}
            <Input
              inputMode="numeric"
              value={form.amount}
              onChange={setField("amount")}
              placeholder={`min ${fmt(config.min_idr)}`}
            />
          </Field>
          <Field>
            {t("cp_payer")}
            <Input value={form.payer_name} onChange={setField("payer_name")} />
          </Field>
          <Field>
            {t("cp_phone")}
            <Input
              inputMode="numeric"
              value={form.payer_phone}
              onChange={setField("payer_phone")}
            />
          </Field>
          <Field>
            {t("cp_email")}
            <Input
              value={form.payer_email}
              onChange={setField("payer_email")}
            />
          </Field>
        </Grid>
        <Field style={{ marginTop: "0.7rem" }}>
          {t("cp_description")}
          <TextArea
            maxLength={config.description_limit || 200}
            value={form.description}
            onChange={setField("description")}
          />
        </Field>
        <div style={{ marginTop: "0.7rem" }}>
          <Button
            type="button"
            disabled={!canSubmit || busy !== null}
            onClick={submit}
          >
            {t("cp_create")}
          </Button>
          {amountValue > (config.review_idr || Infinity) &&
          !config.can_high_value ? (
            <span
              style={{
                color: "#8a5a00",
                fontWeight: 700,
                marginLeft: "0.6rem",
              }}
            >
              {t("cp_needs_admin")}
            </span>
          ) : null}
        </div>
      </Card>

      <Card>
        <FilterBar>
          <FilterField>
            {t("cp_f_search")}
            <SmallInput
              value={filters.q || ""}
              onChange={setFilter("q")}
              placeholder={t("cp_f_search_ph")}
              onKeyDown={(event) => event.key === "Enter" && applyFilters()}
            />
          </FilterField>
          <FilterField>
            {t("cp_f_status")}
            <SmallSelect
              value={filters.status || ""}
              onChange={setFilter("status")}
            >
              <option value="">{t("cp_f_all")}</option>
              {(props.statuses || []).map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </SmallSelect>
          </FilterField>
          <FilterField>
            {t("cp_f_from")}
            <SmallInput
              type="date"
              value={filters.from || ""}
              onChange={setFilter("from")}
            />
          </FilterField>
          <FilterField>
            {t("cp_f_to")}
            <SmallInput
              type="date"
              value={filters.to || ""}
              onChange={setFilter("to")}
            />
          </FilterField>
          <FilterField>
            {t("cp_f_min")}
            <SmallInput
              inputMode="numeric"
              style={{ width: "110px" }}
              value={filters.min || ""}
              onChange={setFilter("min")}
            />
          </FilterField>
          <FilterField>
            {t("cp_f_max")}
            <SmallInput
              inputMode="numeric"
              style={{ width: "110px" }}
              value={filters.max || ""}
              onChange={setFilter("max")}
            />
          </FilterField>
          <CheckLabel>
            <input
              type="checkbox"
              checked={Boolean(filters.mine)}
              onChange={setFilter("mine")}
            />
            {t("cp_f_mine")}
          </CheckLabel>
          <TinyButton type="button" $tone="link" onClick={applyFilters}>
            <Filter size={12} />
            {t("cp_f_apply")}
          </TinyButton>
          <TinyButton type="button" $tone="neutral" onClick={clearFilters}>
            <X size={12} />
            {t("cp_f_clear")}
          </TinyButton>
        </FilterBar>
        {rows.length === 0 ? (
          <Meta>{t("cp_empty")}</Meta>
        ) : (
          rows.map((row) => (
            <Row key={row.id}>
              <RowTop>
                <div>
                  <Desc>{row.description}</Desc>{" "}
                  <Chip $status={row.status}>{row.status}</Chip>
                  <Meta>
                    {row.payer_name}
                    {row.payer_phone ? ` · ${row.payer_phone}` : ""}
                    {row.payer_email ? ` · ${row.payer_email}` : ""}
                  </Meta>
                  <Meta>
                    {formatDateTime(row.created_at)} · {row.created_by}
                    {row.invoice_no ? ` · ${row.invoice_no}` : ""}
                    {row.void_reason ? ` · ${row.void_reason}` : ""}
                  </Meta>
                </div>
                <Amount>{row.amount_formatted}</Amount>
              </RowTop>
              <div style={{ marginTop: "0.5rem" }}>
                {row.payment_link_url ? (
                  <TinyButton
                    as="a"
                    $tone="link"
                    href={row.payment_link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink size={12} />
                    {t("cp_open_link")}
                  </TinyButton>
                ) : null}
                {row.can_send_wa ? (
                  <TinyButton
                    type="button"
                    disabled={busy !== null}
                    $tone="wa"
                    onClick={() => rowAction(row, "send_whatsapp")}
                  >
                    <MessageCircle size={12} />
                    {t("cp_send_wa")}
                  </TinyButton>
                ) : null}
                {row.can_sync ? (
                  <TinyButton
                    type="button"
                    disabled={busy !== null}
                    $tone="sync"
                    onClick={() => rowAction(row, "sync")}
                  >
                    <RefreshCw size={12} />
                    {t("cp_sync")}
                  </TinyButton>
                ) : null}
                {row.can_recreate ? (
                  <TinyButton
                    type="button"
                    disabled={busy !== null}
                    $tone="recreate"
                    onClick={() => recreateRow(row)}
                  >
                    <RotateCcw size={12} />
                    {t("cp_recreate")}
                  </TinyButton>
                ) : null}
                {row.can_void ? (
                  <TinyButton
                    type="button"
                    $tone="danger"
                    disabled={busy !== null}
                    onClick={() => voidRow(row)}
                  >
                    <Ban size={12} />
                    {t("cp_void")}
                  </TinyButton>
                ) : null}
                <TinyButton
                  type="button"
                  disabled={busy !== null}
                  $tone="neutral"
                  onClick={() => editEmail(row)}
                >
                  <Mail size={12} />
                  {t("cp_edit_email")}
                </TinyButton>
                {row.invoice_pdf ? (
                  <TinyButton
                    as="a"
                    $tone="neutral"
                    href={`/admin/online_payments/${row.id}/invoice_pdf?type=CustomPayment`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FileText size={12} />
                    {t("cp_invoice")}
                  </TinyButton>
                ) : null}
              </div>
            </Row>
          ))
        )}
        {pagination.total > 0 ? (
          <Pager>
            <TinyButton
              type="button"
              $tone="neutral"
              disabled={pagination.page <= 1}
              onClick={() => go(1)}
            >
              ««
            </TinyButton>
            <TinyButton
              type="button"
              $tone="neutral"
              disabled={pagination.page <= 1}
              onClick={() => go(pagination.page - 1)}
            >
              ‹
            </TinyButton>
            {/* The range, not just the page number: "31–55 of 210" answers "how far in am I" without
                the FO doing arithmetic. */}
            <PageInfo>
              {pagination.from}–{pagination.to} {t("cp_of")} {pagination.total}
            </PageInfo>
            <TinyButton
              type="button"
              $tone="neutral"
              disabled={pagination.page >= pagination.pages}
              onClick={() => go(pagination.page + 1)}
            >
              ›
            </TinyButton>
            <TinyButton
              type="button"
              $tone="neutral"
              disabled={pagination.page >= pagination.pages}
              onClick={() => go(pagination.pages)}
            >
              »»
            </TinyButton>
          </Pager>
        ) : null}
      </Card>

      {confirm ? (
        <ConfirmDialog
          title={confirm.title}
          body={confirm.body}
          confirmLabel={t("confirm_ok")}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      ) : null}
    </PageShell>
  );
};

export default CustomPaymentsPage;
